import { cn } from "../shared/ui/index.ts";
import * as React from "react";
import { appOpenPatient } from "../core/app-bridge.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import {
  patientsService,
  stubPatient,
} from "../core/services/patients.service.ts";
import { I } from "../shared/icons/icons.tsx";
import { PatientNameDisplay } from "../shared/patient/patient-name-display.component.tsx";
import {
  pg,
  pgBarActions,
  pgBarStart,
  secondaryBtnLg,
} from "../shared/page-layout.ts";
export function FirstAppointmentPage() {
  const initial = [
    {
      id: 1,
      name: "Julien Petit",
      when: "Aujourd'hui · 10:00",
      age: 42,
      motif: "Premier rendez-vous",
      step: 2,
      total: 4,
      status: "next",
    },
    {
      id: 2,
      name: "Léa Moreau",
      when: "Mer 29 avril · 08:30",
      age: 34,
      motif: "Premier rendez-vous",
      step: 1,
      total: 4,
      status: "scheduled",
    },
    {
      id: 3,
      name: "Hugo Martin",
      when: "Mer 29 avril · 09:30",
      age: 45,
      motif: "Premier rendez-vous",
      step: 0,
      total: 4,
      status: "scheduled",
    },
    {
      id: 4,
      name: "Sarah Vidal",
      when: "Jeu 30 avril · 14:00",
      age: 28,
      motif: "Premier rendez-vous",
      step: 0,
      total: 4,
      status: "scheduled",
    },
  ];
  type AppointmentItem = (typeof initial)[number];
  const [list, setList] = React.useState(initial);
  const toast = Interactions.useToast();
  const steps = [
    "Documents reçus",
    "Antécédents complétés",
    "Consultation",
    "Compte-rendu envoyé",
  ];

  const advance = (id: number, idx: number) => {
    setList((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, step: Math.min(idx + 1, p.total) } : p,
      ),
    );
    toast.push({
      type: "success",
      title: "Étape validée",
      message: steps[idx],
    });
  };
  const openFile = (p: AppointmentItem) => {
    const patient =
      patientsService.findByName(p.name) ??
      stubPatient({
        uuid: `00000000-0000-4000-8000-${String(p.id).padStart(12, "0")}`,
        name: p.name,
        age: p.age,
        sex: "M",
        phone: "—",
        last: "—",
        tag: "Premier RDV",
        color: "#8b5cf6",
      });
    appOpenPatient(patient);
  };
  return (
    <div className={pg.page}>
      <div className={pg.toolbar}>
        <p
          className={cn(pgBarStart, "truncate text-[13px] text-sf-text-muted")}
        >
          <span className="font-bold text-sf-text">{list.length}</span> nouveaux
          patients en cours d&apos;intégration
        </p>
        <div className={pgBarActions}>
          <button type="button" className={secondaryBtnLg()}>
            <I.UserPlus size={14} /> Nouveau premier RDV
          </button>
        </div>
      </div>
      <div className={cn(pg.content, "flex flex-col gap-3.5")}>
        {list.map((p) => (
          <div
            key={p.id}
            className="rounded-lg border border-sf-border bg-white p-[18px] shadow-[var(--sf-shadow-sm)]"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-1.5 text-[15px]">
                  <PatientNameDisplay
                    name={p.name}
                    firstClassName="font-semibold"
                  />
                  <span className="font-medium text-sf-text-muted">
                    · {p.age} ans
                  </span>
                </div>
                <div className="mt-0.5 text-[12.5px] text-sf-text-muted">
                  {p.motif} · {p.when}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.status === "next" && (
                  <span className="rounded border border-sf-primary-100 bg-sf-primary-50 px-2.5 py-1 text-[11px] font-semibold text-sf-primary">
                    Prochain
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => openFile(p)}
                  className={secondaryBtnLg()}
                >
                  Voir le dossier
                </button>
              </div>
            </div>
            <div className="flex items-center gap-0 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {steps.map((s, i) => {
                const done = i < p.step;
                const current = i === p.step;
                return (
                  <React.Fragment key={i}>
                    <div
                      role={current ? "button" : undefined}
                      tabIndex={current ? 0 : undefined}
                      onClick={() => current && advance(p.id, i)}
                      onKeyDown={(e) => {
                        if (current && (e.key === "Enter" || e.key === " ")) {
                          e.preventDefault();
                          advance(p.id, i);
                        }
                      }}
                      className={cn(
                        "flex shrink-0 flex-col items-center gap-1.5",
                        current ? "group cursor-pointer" : "cursor-default",
                      )}
                      title={current ? "Cliquer pour valider l'étape" : ""}
                    >
                      <div
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-transform duration-150",
                          done && "bg-sf-success text-white",
                          current &&
                            "bg-sf-primary text-white shadow-[0_0_0_4px_var(--sf-primary-50)] group-hover:scale-110",
                          !done &&
                            !current &&
                            "border-[1.5px] border-sf-border bg-white text-sf-text-muted",
                        )}
                      >
                        {done ? <I.Check size={13} /> : i + 1}
                      </div>
                      <div
                        className={cn(
                          "whitespace-nowrap text-[11px]",
                          done && "font-semibold text-sf-success",
                          current && "font-semibold text-sf-primary",
                          !done && !current && "font-medium text-sf-text-muted",
                        )}
                      >
                        {s}
                      </div>
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        className={cn(
                          "-mt-[22px] h-0.5 flex-1",
                          i < p.step ? "bg-sf-success" : "bg-sf-border",
                        )}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
